// src/app/components/chat/ChatMemberPanel.tsx
//
// Painel lateral simples com os participantes de um grupo. Colapsável via
// prop `open` — controlado pelo componente pai (ChatThread/Chat.tsx).

import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { Camera, LogOut, UserPlus, Users, X } from 'lucide-react';
import { toast } from 'sonner';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Skeleton } from '../ui/skeleton';
import { chatService, type ChatGroup, type ChatParticipant } from '../../services/chatService';
import { getUserInitials } from './chatUtils';
import ColleaguePicker from './ColleaguePicker';

export interface ChatMemberPanelProps {
  group: ChatGroup;
  open: boolean;
  currentUserId: number;
  /** true para superuser/staff/manager/coordinator — mesmo cálculo de
   * `canDeleteAny` em Chat.tsx. Combinado com `group.created_by`, decide
   * quem pode remover OUTROS participantes (ver chat_group_viewset.py). */
  isPrivileged: boolean;
  onClose: () => void;
  /** Propaga o grupo atualizado (ex: novo avatar_url) pro estado do pai. */
  onGroupUpdated?: (group: ChatGroup) => void;
  /** Chamado após o usuário logado sair do grupo com sucesso — o pai deve
   * remover o grupo da lista local e selecionar outra conversa. */
  onLeft?: () => void;
}

export default function ChatMemberPanel({
  group,
  open,
  currentUserId,
  isPrivileged,
  onClose,
  onGroupUpdated,
  onLeft,
}: ChatMemberPanelProps) {
  const [members, setMembers] = useState<ChatParticipant[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [pendingUserId, setPendingUserId] = useState<number | null>(null);
  const [leaving, setLeaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const canManageMembers = group.kind === 'group';
  // Só quem criou o grupo, ou um privilegiado, pode remover OUTROS
  // participantes — mesma regra do backend (remove_participant_action).
  // Grupo sem created_by (criado antes deste campo existir) só libera
  // pra privilegiado. Remover a si mesmo (sair) nunca depende disso.
  const isOwner = group.created_by?.id === currentUserId;
  const canRemoveOthers = isPrivileged || isOwner;

  const fetchMembers = () => {
    setMembers(null);
    setFailed(false);
    chatService
      .getGroupMembers(group.id)
      .then((list) => setMembers(list))
      .catch(() => {
        setFailed(true);
        setMembers([]);
      });
  };

  useEffect(() => {
    if (!open) return;
    fetchMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group.id, open]);

  if (!open) return null;

  const handleAdd = async (userId: number) => {
    setPendingUserId(userId);
    try {
      await chatService.addParticipant(group.id, userId);
      fetchMembers();
      setAddOpen(false);
    } finally {
      setPendingUserId(null);
    }
  };

  const handleRemove = async (userId: number) => {
    setPendingUserId(userId);
    try {
      await chatService.removeParticipant(group.id, userId);
      if (userId === currentUserId) {
        onLeft?.();
        return;
      }
      fetchMembers();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Não foi possível remover o participante.');
    } finally {
      setPendingUserId(null);
    }
  };

  const handleLeave = async () => {
    if (!window.confirm('Tem certeza que deseja sair desta conversa?')) return;
    setLeaving(true);
    try {
      await chatService.removeParticipant(group.id, currentUserId);
      onLeft?.();
    } catch {
      toast.error('Não foi possível sair da conversa.');
    } finally {
      setLeaving(false);
    }
  };

  const handleAvatarChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    setUploadingAvatar(true);
    try {
      const updated = await chatService.updateGroupAvatar(group.id, file);
      onGroupUpdated?.(updated);
    } catch {
      toast.error('Não foi possível atualizar a foto do grupo.');
    } finally {
      setUploadingAvatar(false);
    }
  };

  return (
    <div className="w-60 flex-shrink-0 border-l border-slate-200 dark:border-white/[0.08] bg-white dark:bg-[#0a0b0d] flex flex-col">
      {canManageMembers && (
        <div className="flex flex-col items-center gap-1.5 px-3 pt-4 pb-2 border-b border-slate-200 dark:border-white/[0.08]">
          <button
            type="button"
            onClick={() => avatarInputRef.current?.click()}
            disabled={uploadingAvatar}
            className="group relative"
            title="Alterar foto do grupo"
          >
            <Avatar className="size-14">
              {group.avatar_url && <AvatarImage src={group.avatar_url} alt="" />}
              <AvatarFallback className="bg-indigo-100 dark:bg-indigo-500/15 text-indigo-700 dark:text-indigo-300 text-base font-semibold">
                {group.display_name.slice(0, 2).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <span className="absolute inset-0 flex items-center justify-center rounded-full bg-black/0 group-hover:bg-black/40 transition-colors">
              <Camera className="size-4 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
            </span>
          </button>
          <input
            ref={avatarInputRef}
            type="file"
            accept="image/*"
            hidden
            onChange={handleAvatarChange}
          />
          <p className="text-xs font-medium text-slate-700 dark:text-slate-200 truncate max-w-full">{group.display_name}</p>
        </div>
      )}
      <div className="flex items-center justify-between px-3 py-3 border-b border-slate-200 dark:border-white/[0.08]">
        <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-700 dark:text-slate-200">
          <Users className="size-4" />
          Participantes
        </div>
        <div className="flex items-center gap-2">
          {canManageMembers && (
            <Popover open={addOpen} onOpenChange={setAddOpen}>
              <PopoverTrigger asChild>
                <button type="button" className="text-slate-400 dark:text-slate-500 hover:text-indigo-600 dark:hover:text-indigo-400" title="Adicionar membro">
                  <UserPlus className="size-4" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-64 p-0" align="end">
                <ColleaguePicker
                  excludeIds={(members ?? []).map((m) => m.user.id)}
                  onSelect={(u) => handleAdd(u.id)}
                  emptyLabel="Todos os colegas já participam."
                />
              </PopoverContent>
            </Popover>
          )}
          <button type="button" onClick={onClose} className="text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300">
            <X className="size-4" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-2 space-y-1">
        {members === null ? (
          Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-9 w-full rounded-lg dark:bg-white/10" />)
        ) : members.length === 0 ? (
          <p className="text-xs text-slate-400 dark:text-slate-500 text-center py-6 px-2">
            {failed ? 'Erro ao carregar participantes.' : 'Nenhum participante explícito neste grupo.'}
          </p>
        ) : (
          members.map((m) => (
            <div
              key={m.id}
              className="group flex items-center gap-2 px-1.5 py-1.5 rounded-lg hover:bg-slate-50 dark:hover:bg-white/[0.06]"
            >
              <Avatar className="size-7">
                {m.user.avatar_url && <AvatarImage src={m.user.avatar_url} alt="" />}
                <AvatarFallback className="bg-indigo-100 dark:bg-indigo-500/15 text-indigo-700 dark:text-indigo-300 text-[10px] font-semibold">
                  {getUserInitials(m.user)}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium text-slate-700 dark:text-slate-200 truncate">{m.user.full_name}</p>
                <p className="text-[10px] text-slate-400 dark:text-slate-500 truncate">@{m.user.username}</p>
              </div>
              {canManageMembers && (m.user.id === currentUserId || canRemoveOthers) && (
                <button
                  type="button"
                  onClick={() => handleRemove(m.user.id)}
                  disabled={pendingUserId === m.user.id}
                  className="opacity-0 group-hover:opacity-100 text-slate-400 dark:text-slate-500 hover:text-red-600 dark:hover:text-red-400 transition-opacity flex-shrink-0"
                  title={m.user.id === currentUserId ? 'Sair da conversa' : 'Remover'}
                >
                  <X className="size-3.5" />
                </button>
              )}
            </div>
          ))
        )}
      </div>

      {canManageMembers && (
        <div className="border-t border-slate-200 dark:border-white/[0.08] p-2">
          <button
            type="button"
            onClick={() => void handleLeave()}
            disabled={leaving}
            className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors disabled:opacity-50"
          >
            <LogOut className="size-3.5" />
            {leaving ? 'Saindo...' : 'Sair da conversa'}
          </button>
        </div>
      )}
    </div>
  );
}
